const SITE_URL = 'https://fussaki.com';
const BUSINESS_NAME = 'Fussaki Spearfishing & Sportfishing Charters';

export function getLocalBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#business`,
    name: BUSINESS_NAME,
    image: `${SITE_URL}/logo512.png`,
    description:
      'Premium spearfishing and sportfishing charters out of San Pedro, CA. Captain-led trips targeting White Seabass, Yellowtail, Bluefin Tuna across Southern California.',
    url: SITE_URL,
    telephone: '(714) 200-9815',
    email: 'fussaki@gmail.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '224 Whalers Walk',
      addressLocality: 'San Pedro',
      addressRegion: 'CA',
      postalCode: '90731',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 33.7353,
      longitude: -118.278,
    },
    areaServed: [
      'Southern California',
      'Los Angeles',
      'San Pedro',
      'Catalina Island',
      'San Clemente Island',
    ],
    priceRange: '$1,799 - $3,499+',
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ],
      opens: '05:30',
      closes: '18:00',
    },
    sameAs: ['https://www.instagram.com/fussaki/'],
  };
}

export function getServiceSchema(
  name: string,
  description: string,
  url: string,
  options?: {
    offers?: Array<{ name: string; price: string }>;
  },
) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    url: `${SITE_URL}${url}`,
    provider: { '@id': `${SITE_URL}/#business` },
    areaServed: 'Southern California',
    serviceType: 'Fishing Charter',
  };

  if (options?.offers && options.offers.length > 0) {
    // Parse "From $2,999" → 2999 or leave as text if unparseable
    const offerItems = options.offers
      .map((o) => {
        const match = o.price.match(/\$?([\d,]+)/);
        if (!match) return null;
        const priceNum = match[1].replace(/,/g, '');
        return {
          '@type': 'Offer',
          name: o.name,
          price: priceNum,
          priceCurrency: 'USD',
          url: `${SITE_URL}${url}`,
        };
      })
      .filter(Boolean);

    if (offerItems.length === 1) {
      schema.offers = offerItems[0];
    } else if (offerItems.length > 1) {
      schema.offers = {
        '@type': 'AggregateOffer',
        lowPrice: Math.min(
          ...offerItems.map((o) => Number((o as { price: string }).price)),
        ).toString(),
        highPrice: Math.max(
          ...offerItems.map((o) => Number((o as { price: string }).price)),
        ).toString(),
        priceCurrency: 'USD',
        offerCount: offerItems.length,
        offers: offerItems,
      };
    }
  }

  return schema;
}

export function getFAQSchema(
  faqs: Array<{ question: string; answer: string }>,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * Convert a locally-imported image path to an absolute OG-compatible URL.
 * In production, imported images resolve to `/static/media/...` — this
 * prepends the site domain so og:image / twitter:image get a full URL.
 */
export function getOgImageUrl(imagePath: string): string {
  return `${SITE_URL}${imagePath}`;
}

/**
 * Known dimensions for OG images used across the site.
 * Keys are the image import variable names from imageAssets.ts.
 * Used for og:image:width / og:image:height meta tags.
 */
export const OG_IMAGE_DIMENSIONS: Record<string, { width: number; height: number }> = {
  heroUnderwaterBluefin: { width: 1920, height: 1440 },
  heroChristosGrouper: { width: 1200, height: 1600 },
  heroBluefinDeck: { width: 1400, height: 1657 },
  boatWorldcatAerial: { width: 1600, height: 900 },
  heroWsbDiverKelpDark: { width: 1920, height: 1281 },
  boatWorldcatRear: { width: 602, height: 780 },
  catalinaIslandHarbor: { width: 1920, height: 860 },
  diverYellowtailAscending: { width: 1200, height: 1296 },
  diverBluefinBelow: { width: 1920, height: 1440 },
  heroWsbKelp: { width: 1600, height: 1102 },
  diverWsbUnderwater: { width: 1200, height: 900 },
  groupWsbCatch: { width: 1200, height: 900 },
};

/**
 * Look up OG image dimensions by image import path.
 * Falls back to undefined if the path is not in the known map.
 */
export function getOgImageDimensions(
  imagePath: string,
): { width: number; height: number } | undefined {
  // The image path is a Webpack-resolved import — match it against known keys
  // by checking if the path includes the identifier substring.
  const keyMap: Record<string, string> = {
    'hero-underwater-bluefin': 'heroUnderwaterBluefin',
    'hero-christos-grouper': 'heroChristosGrouper',
    'hero-bluefin-deck': 'heroBluefinDeck',
    'boat-worldcat-aerial': 'boatWorldcatAerial',
    'hero-wsb-diver-kelp-dark': 'heroWsbDiverKelpDark',
    'boat-worldcat-rear': 'boatWorldcatRear',
    'catalina-island-harbor': 'catalinaIslandHarbor',
    'diver-yellowtail-ascending': 'diverYellowtailAscending',
    'diver-bluefin-below': 'diverBluefinBelow',
    'hero-wsb-kelp': 'heroWsbKelp',
    'diver-wsb-underwater': 'diverWsbUnderwater',
    'group-wsb-catch': 'groupWsbCatch',
  };

  for (const [fileFragment, key] of Object.entries(keyMap)) {
    if (imagePath.includes(fileFragment)) {
      return OG_IMAGE_DIMENSIONS[key];
    }
  }
  return undefined;
}

/**
 * BreadcrumbList structured data for Google rich results.
 * @param items — ordered array of breadcrumb items with name and relative URL
 */
export function getBreadcrumbSchema(
  items: Array<{ name: string; url: string }>,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  };
}

/**
 * ContactPoint structured data — enriches the LocalBusiness graph with
 * explicit phone / email contact details for the Contact page.
 */
export function getContactPointSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#business`,
    name: BUSINESS_NAME,
    url: SITE_URL,
    telephone: '(714) 200-9815',
    email: 'fussaki@gmail.com',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+1-714-200-9815',
        email: 'fussaki@gmail.com',
        contactType: 'reservations',
        areaServed: 'US',
        availableLanguage: 'English',
      },
    ],
    address: {
      '@type': 'PostalAddress',
      streetAddress: '224 Whalers Walk',
      addressLocality: 'San Pedro',
      addressRegion: 'CA',
      postalCode: '90731',
      addressCountry: 'US',
    },
  };
}
